import { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_VARIANTS,
  UI,
  formatRelativeDate,
} from '@/lib/constants'
import { useCancelReferral } from '@/hooks'
import type { Referral } from '@/lib/types'
import { UserPlus, XCircle, AlertCircle, AlertTriangle } from 'lucide-react'
import { ExternalLink } from '@/components/shared/external-link'
import { EmptyState } from '@/components/shared/empty-state'
import { SortableHeader, type SortDirection } from '@/components/shared/sortable-header'
import { getOrderUrl, getSourceLabel } from '@/lib/external-urls'

interface ReferralTableProps {
  data?: Referral[]
  isLoading: boolean
  sortKey?: string | null
  sortDirection?: SortDirection
  onSort?: (key: string, direction: SortDirection) => void
  onClearFilters?: () => void
}

function CancelReferralDialog({
  referral,
  open,
  onOpenChange,
}: {
  referral: Referral | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [reason, setReason] = useState('')
  const { mutate: cancelReferral, isPending } = useCancelReferral()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!referral || reason.trim().length < 3) return

    cancelReferral(
      { id: referral.id, reason: reason.trim() },
      {
        onSuccess: () => {
          setReason('')
          onOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Cancelar indicação</DialogTitle>
            <DialogDescription>
              Isso irá cancelar a indicação de <strong>{referral?.refereeName}</strong> e estornar a
              comissão do membro indicador.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reason">Motivo do cancelamento</Label>
            <Input
              id="reason"
              placeholder="Ex: Devolução total do pedido"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2"
              autoFocus
            />
            {reason.length > 0 && reason.length < 3 && (
              <p className="text-sm text-destructive mt-1">Mínimo de 3 caracteres</p>
            )}
          </div>
          <DialogFooter>
            <ChonkyButton type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Voltar
            </ChonkyButton>
            <ChonkyButton
              type="submit"
              variant="destructive"
              disabled={reason.trim().length < 3}
              loading={isPending}
            >
              Cancelar indicação
            </ChonkyButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TableRowSkeleton() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton className="h-4 w-20" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-28" />
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <Skeleton className="h-4 w-16" />
      </TableCell>
      <TableCell className="text-right">
        <Skeleton className="h-4 w-16 ml-auto" />
      </TableCell>
      <TableCell className="hidden sm:table-cell text-right">
        <Skeleton className="h-4 w-14 ml-auto" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-20" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-8 w-8" />
      </TableCell>
    </TableRow>
  )
}

export function ReferralTable({
  data,
  isLoading,
  sortKey,
  sortDirection,
  onSort,
  onClearFilters,
}: ReferralTableProps) {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null)

  const handleCancelClick = (referral: Referral) => {
    setSelectedReferral(referral)
    setCancelDialogOpen(true)
  }

  if (!isLoading && data?.length === 0) {
    return (
      <div className="rounded-md border bg-card shadow-sm">
        <EmptyState
          icon={UserPlus}
          title="Nenhuma indicação encontrada"
          description="As indicações aparecem aqui quando clientes usam cupons de indicação em suas compras."
          actionLabel="Limpar filtros"
          onAction={onClearFilters}
        />
      </div>
    )
  }

  return (
    <>
      <div className="max-w-[calc(100vw-2rem)] md:max-w-[calc(100vw-theme(spacing.64)-3rem)]">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                sortKey="createdAt"
                currentSort={sortKey}
                currentDirection={sortDirection}
                onSort={onSort}
                className="w-24"
              >
                Data
              </SortableHeader>
              <TableHead className="max-w-35">Indicador</TableHead>
              <TableHead className="hidden md:table-cell max-w-45">Indicado</TableHead>
              <TableHead className="hidden lg:table-cell">Pedido</TableHead>
              <SortableHeader
                sortKey="orderAmount"
                currentSort={sortKey}
                currentDirection={sortDirection}
                onSort={onSort}
                className="text-right"
              >
                Valor
              </SortableHeader>
              <SortableHeader
                sortKey="rewardAmount"
                currentSort={sortKey}
                currentDirection={sortDirection}
                onSort={onSort}
                className="hidden sm:table-cell text-right"
              >
                Recompensa
              </SortableHeader>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: UI.SKELETON_TABLE_ROWS }).map((_, i) => (
                <TableRowSkeleton key={i} />
              ))
            ) : data?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <EmptyState
                    icon={UserPlus}
                    title="Nenhuma indicação encontrada"
                    description="As indicações aparecem aqui quando clientes usam cupons de indicação em suas compras."
                    className="sticky left-0 w-[calc(100vw-2rem)] md:w-[calc(100vw-theme(spacing.64)-3rem)]"
                  />
                </TableCell>
              </TableRow>
            ) : (
              data?.map((referral) => {
                const orderUrl = getOrderUrl(
                  referral.source,
                  referral.externalOrderId,
                  referral.marketplace
                )
                const sourceLabel = getSourceLabel(referral.source)

                return (
                  <TableRow key={referral.id}>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatRelativeDate(referral.createdAt)}
                    </TableCell>
                    <TableCell className="font-medium max-w-35 truncate">
                      {referral.referrer?.name || 'Desconhecido'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell max-w-45">
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{referral.refereeName}</span>
                        <span className="text-xs text-muted-foreground truncate">
                          {referral.refereeEmail}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {orderUrl ? (
                        <ExternalLink
                          href={orderUrl}
                          platform={sourceLabel}
                          className="text-muted-foreground hover:text-primary no-underline hover:underline"
                        >
                          #{referral.externalOrderId}
                        </ExternalLink>
                      ) : (
                        <span className="text-muted-foreground">#{referral.externalOrderId}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      R$ {referral.orderAmount.toFixed(2)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-right font-medium text-green-600">
                      R$ {referral.rewardAmount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {(referral.status === 'rejected' || referral.status === 'cancelled') &&
                        referral.autoRefundStatus === 'failed' ? (
                          <Badge variant="destructive" className="w-fit flex gap-1 items-center">
                            <AlertTriangle className="h-3 w-3" />
                            Falha no Estorno
                          </Badge>
                        ) : (
                          <Badge
                            variant={
                              REFERRAL_STATUS_VARIANTS[
                                referral.status as keyof typeof REFERRAL_STATUS_VARIANTS
                              ]
                            }
                            className="w-fit"
                          >
                            {
                              REFERRAL_STATUS_LABELS[
                                referral.status as keyof typeof REFERRAL_STATUS_LABELS
                              ]
                            }
                          </Badge>
                        )}
                        {(referral.status === 'rejected' || referral.status === 'cancelled') &&
                          (referral.rejectionReason ||
                            referral.cancellationReason ||
                            referral.autoRefundError) && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <AlertCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="max-w-xs flex flex-col gap-2">
                                    {(referral.rejectionReason || referral.cancellationReason) && (
                                      <p>
                                        {referral.rejectionReason || referral.cancellationReason}
                                      </p>
                                    )}
                                    {referral.autoRefundStatus === 'failed' && (
                                      <div
                                        className={
                                          referral.rejectionReason ||
                                          referral.cancellationReason ||
                                          referral.autoRefundError
                                            ? 'mt-1 pt-2 border-t border-border/50 font-medium text-destructive'
                                            : 'font-medium text-destructive'
                                        }
                                      >
                                        <p className="text-xs uppercase mb-1">Erro no Gateway:</p>
                                        {referral.autoRefundError && (
                                          <p>{referral.autoRefundError}</p>
                                        )}
                                        <p className="mt-2 text-xs">
                                          Acesse o painel do seu checkout para estornar manualmente.
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {referral.status === 'pending' && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleCancelClick(referral)}
                              >
                                <XCircle className="h-4 w-4" />
                                <span className="sr-only">Cancelar indicação</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Cancelar indicação</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      <CancelReferralDialog
        referral={selectedReferral}
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
      />
    </>
  )
}
